// ============================================================================
// api_deviceWebRTC.ts
// WebRTC côté device — PeerConnection, Offer/Answer, codec forcing
// v3.0 — Perfect Negotiation (polite peer) + logs détaillés ICE/SDP
// ============================================================================

import { DeviceWebRTCRefs, DeviceWebRTCCallbacks } from '../../dataTypes/liveStreamManagment/types_callbacks';
import { RequiredCodec } from '../../dataTypes/liveStreamManagment/types_media';
import { parseSdpCodec, extractCodecFromSdp, forceSpecificCodecInSdp } from '../../dataTypes/liveStreamManagment/utils_sdp';
import { defaultRTCConfig } from '../../dataTypes/liveStreamManagment/utils_webrtc';

import { sendDeviceIceCandidate, sendDeviceAnswer, sendDeviceOffer } from './api_deviceSignaling';

import type { MutableRefObject } from 'react';

// ============================================================================
// SECTION 0 — Helpers de log ICE et SDP
// ============================================================================

/**
 * Résume un ICE candidate en une ligne lisible.
 * Exemple : "relay udp 62.210.101.187:61476 (prio=3|32|255)"
 */
const summarizeIceCandidate = (candidate: RTCIceCandidate | RTCIceCandidateInit): string => {
  // RTCIceCandidate expose les champs directement ; RTCIceCandidateInit a candidate (string)
  const raw = (candidate as RTCIceCandidate).candidate
           ?? (candidate as RTCIceCandidateInit).candidate
           ?? '';
  if (!raw) return '(fin gathering — null candidate)';

  // Format SDP : "candidate:xxx udp/tcp prio ip port typ host/srflx/relay ..."
  const parts = raw.split(' ');
  const protocol = parts[2]?.toLowerCase() ?? '?';
  const address  = parts[4] ?? '?';
  const port     = parts[5] ?? '?';
  const typIdx   = parts.indexOf('typ');
  const type     = typIdx >= 0 ? parts[typIdx + 1] : '?';

  // Pour relay, on récupère aussi raddr (adresse reflexive d'origine)
  let extra = '';
  if (type === 'relay' || type === 'srflx') {
    const raddrIdx = parts.indexOf('raddr');
    const rportIdx = parts.indexOf('rport');
    if (raddrIdx >= 0 && rportIdx >= 0) {
      extra = ` (via ${parts[raddrIdx + 1]}:${parts[rportIdx + 1]})`;
    }
  }

  // Priorité — utile pour comprendre quelle paire ICE sera choisie
  const prio = parts[3] ?? '?';

  return `${type} ${protocol} ${address}:${port}${extra} [prio=${prio}]`;
};

/**
 * Résume un SDP en une ligne lisible.
 * Extrait : codecs, direction, nb de m-lines, ufrag ICE, fingerprint présent/absent.
 */
const summarizeSdp = (sdp: string | undefined | null, label: string): string => {
  if (!sdp) return `${label} — SDP vide`;

  const lines = sdp.split('\n');

  // Codecs vidéo avec profil H264
  const videoCodecs: string[] = [];
  const audioCodecs: string[] = [];
  lines.forEach(l => {
    const m = l.match(/^a=rtpmap:\d+ ([^/]+)/);
    if (!m) return;
    const codec = m[1].toUpperCase();
    // On considère que les codecs vidéo courants sont H264, VP8, VP9, AV1
    if (['H264', 'VP8', 'VP9', 'AV1', 'H265'].includes(codec)) {
      videoCodecs.push(codec);
    } else if (['OPUS', 'PCMU', 'PCMA', 'G722', 'ISAC'].includes(codec)) {
      audioCodecs.push(codec);
    }
  });

  // Profils H264 (fmtp profile-level-id)
  const h264Profiles: string[] = [];
  lines.forEach(l => {
    const m = l.match(/profile-level-id=([0-9a-fA-F]{6})/);
    if (m) {
      const pid = m[1].toLowerCase();
      const profileByte = pid.slice(0, 2);
      const levelByte   = parseInt(pid.slice(4, 6), 16);
      const level = `${Math.floor(levelByte / 10)}.${levelByte % 10}`;
      const name = profileByte === '42' ? 'Baseline'
                 : profileByte === '4d' ? 'Main'
                 : profileByte === '64' ? 'High'
                 : `0x${profileByte}`;
      if (!h264Profiles.includes(`${name} ${level} (${pid})`)) {
        h264Profiles.push(`${name} ${level} (${pid})`);
      }
    }
  });

  // Direction
  const directions: string[] = [];
  lines.forEach(l => {
    if (l.startsWith('a=sendrecv')) directions.push('sendrecv');
    else if (l.startsWith('a=sendonly')) directions.push('sendonly');
    else if (l.startsWith('a=recvonly')) directions.push('recvonly');
    else if (l.startsWith('a=inactive')) directions.push('inactive');
  });

  // m-lines
  const mlines = lines.filter(l => l.startsWith('m=')).map(l => l.split(' ')[0].slice(2));

  // ICE ufrag (identifie la session ICE — change à chaque renégociation)
  const ufragLine = lines.find(l => l.startsWith('a=ice-ufrag:'));
  const ufrag = ufragLine ? ufragLine.slice('a=ice-ufrag:'.length).trim() : '?';

  // Fingerprint (DTLS)
  const hasFingerprint = lines.some(l => l.startsWith('a=fingerprint:'));

  // Candidats ICE inline dans le SDP (cas trickle = 0)
  const inlineCandidates = lines.filter(l => l.startsWith('a=candidate:')).length;

  const videoStr = videoCodecs.length
    ? videoCodecs.join(',') + (h264Profiles.length ? ` [${h264Profiles.join(' | ')}]` : '')
    : 'aucun';
  const audioStr = audioCodecs.length ? audioCodecs.join(',') : 'aucun';
  const dirStr = Array.from(new Set(directions)).join('/') || '?';
  const mlinesStr = mlines.join('+') || '?';
  const candStr  = inlineCandidates > 0 ? ` | ${inlineCandidates} candidats inline` : '';

  return `${label} | m-lines:${mlinesStr} dir:${dirStr} | vidéo:${videoStr} | audio:${audioStr} | ufrag:${ufrag}${candStr} | dtls:${hasFingerprint ? '✅' : '❌'}`;
};

// ============================================================================
// SECTION A — Forçage codec côté device
// ============================================================================

export const forceDeviceCodec = (
  sdp: string,
  required: RequiredCodec | null
): string => {
  const codec         = required?.codec   ?? 'H264';
  const profilePrefix = required?.profile ?? '42e0';
  return forceSpecificCodecInSdp(sdp, codec, profilePrefix);
};

// ============================================================================
// SECTION B — Factory PeerConnection côté device
// ============================================================================

export const makeDevicePeerConnection = (
  refs: DeviceWebRTCRefs,
  callbacks: DeviceWebRTCCallbacks
): RTCPeerConnection => {
  refs.iceCandidateBufferRef.current = [];
  refs.remoteDescSetRef.current      = false;

  const pc = new RTCPeerConnection(defaultRTCConfig());

  console.log('[DEVICE-PC] 🔧 Nouvelle PeerConnection créée');
  callbacks.addSdpLog({ direction: 'info', type: 'event', label: '🔧 Nouvelle PeerConnection créée' });

  // ── Réception DataChannel keepalive ──────────────────────────────────────
  // La régie crée un DataChannel 'keepalive' et envoie des pings toutes les 5s.
  // Le device répond par des pongs pour maintenir le chemin réseau NAT actif
  // et permettre la détection des déconnexions silencieuses.
  pc.ondatachannel = (event) => {
    const dc = event.channel;
    console.log(`[DEVICE-PC] 💓 DataChannel reçu: label="${dc.label}"`);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `💓 Keepalive DC reçu (${dc.label})` });

    dc.onmessage = () => {
      try {
        if (dc.readyState === 'open') dc.send('pong');
      } catch { /* silencieux */ }
    };

    dc.onopen = () => {
      console.log('[DEVICE-PC] 💓 Keepalive DC ouvert');
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: `💓 Keepalive DC ouvert` });
    };

    dc.onclose = () => {
      console.log('[DEVICE-PC] 💓 Keepalive DC fermé');
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: `💓 Keepalive DC fermé` });
    };
  };

  // ── ICE candidates sortants ───────────────────────────────────────────────
  pc.onicecandidate = (e) => {
    if (e.candidate) {
      const summary = summarizeIceCandidate(e.candidate);
      console.log(`[DEVICE-PC] 🧊 ICE sortant: ${summary}`);
      callbacks.addSdpLog({
        direction: 'sent',
        type: 'event',
        label: `🧊 ICE sortant → régie: ${summary}`,
      });
      sendDeviceIceCandidate(refs.wsRef, e.candidate);
    } else {
      console.log('[DEVICE-PC] 🧊 ICE gathering terminé (fin des candidats)');
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: '🧊 ICE gathering terminé — tous les candidats envoyés' });
    }
  };

  // ── États ICE ─────────────────────────────────────────────────────────────
  pc.oniceconnectionstatechange = () => {
    const s = pc.iceConnectionState;
    const icon = s === 'connected' || s === 'completed' ? '✅'
               : s === 'failed'     ? '❌'
               : s === 'disconnected' ? '⚠️'
               : '🔄';
    console.log(`[DEVICE-PC] 🧊 ICE connection: ${s}`);
    callbacks.addSdpLog({
      direction: s === 'failed' || s === 'disconnected' ? 'received' : 'info',
      type: s === 'failed' ? 'error' : 'event',
      label: `${icon} ICE: ${s}`,
    });
  };

  pc.onicegatheringstatechange = () => {
    console.log(`[DEVICE-PC] 🧊 ICE gathering: ${pc.iceGatheringState}`);
    callbacks.addSdpLog({
      direction: 'info',
      type: 'event',
      label: `🧊 ICE gathering: ${pc.iceGatheringState}`,
    });
  };

  // ── État de connexion ─────────────────────────────────────────────────────
  pc.onconnectionstatechange = () => {
    const s = pc.connectionState;
    const icon = s === 'connected'    ? '✅'
               : s === 'failed'       ? '❌'
               : s === 'disconnected' ? '⚠️'
               : s === 'closed'       ? '🔒'
               : '🔄';
    console.log(`[DEVICE-PC] 🔗 connectionState: ${s}`);
    callbacks.addSdpLog({
      direction: s === 'failed' || s === 'disconnected' ? 'received' : 'info',
      type: s === 'failed' ? 'error' : 'event',
      label: `${icon} WebRTC: ${s}`,
    });

    if (s === 'connected') {
      // Log des paires ICE sélectionnées pour savoir si on passe par TURN
      pc.getStats().then(stats => {
        stats.forEach(report => {
          if (report.type === 'candidate-pair' && report.state === 'succeeded' && report.nominated) {
            const localId  = report.localCandidateId;
            const remoteId = report.remoteCandidateId;
            let localInfo  = '';
            let remoteInfo = '';
            stats.forEach(r => {
              if (r.id === localId)  localInfo  = `${r.candidateType} ${r.protocol} ${r.address}:${r.port}`;
              if (r.id === remoteId) remoteInfo = `${r.candidateType} ${r.protocol} ${r.address}:${r.port}`;
            });
            const pairSummary = `local=[${localInfo}] remote=[${remoteInfo}]`;
            console.log(`[DEVICE-PC] ✅ Paire ICE nominée: ${pairSummary}`);
            callbacks.addSdpLog({
              direction: 'info',
              type: 'event',
              label: `✅ Paire ICE nominée: ${pairSummary}`,
            });
          }
        });
      }).catch(() => {/* silencieux — getStats peut échouer sur certains navigateurs */});

      callbacks.onConnected();
    }

    if (s === 'disconnected' || s === 'closed') callbacks.onDisconnected();

    if (s === 'failed') {
      callbacks.onDisconnected();
      if (refs.shouldReconnectRef.current) {
        console.warn('[DEVICE-PC] ⚠️ PC failed — reconnexion dans 2s');
        callbacks.addSdpLog({ direction: 'info', type: 'event', label: '🔄 PC failed → reconnexion dans 2s' });
        setTimeout(() => { if (refs.shouldReconnectRef.current) callbacks.onRetry(); }, 2000);
      }
    }
  };

  // ── État signaling ────────────────────────────────────────────────────────
  pc.onsignalingstatechange = () => {
    console.log(`[DEVICE-PC] 📶 signalingState: ${pc.signalingState}`);
    callbacks.addSdpLog({
      direction: 'info',
      type: 'event',
      label: `📶 Signaling: ${pc.signalingState}`,
    });
  };

  return pc;
};

// ============================================================================
// SECTION C — Offer device → régie (cas device initie)
// ============================================================================

export const createDeviceOffer = async (
  refs: DeviceWebRTCRefs,
  callbacks: DeviceWebRTCCallbacks
): Promise<void> => {
  if (!refs.streamRef.current) {
    console.warn('[DEVICE-PC] ⚠ createDeviceOffer: pas de stream');
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: '⚠ createDeviceOffer: pas de stream disponible' });
    return;
  }
  if (refs.wsRef.current?.readyState !== WebSocket.OPEN) {
    console.warn('[DEVICE-PC] ⚠ createDeviceOffer: WS non ouvert');
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: '⚠ createDeviceOffer: WebSocket non ouvert' });
    return;
  }
  if (refs.pcRef.current) {
    console.log('[DEVICE-PC] 🔄 Fermeture PC existante avant nouvelle Offer');
    refs.pcRef.current.close();
    refs.pcRef.current = null;
  }

  const pc = makeDevicePeerConnection(refs, callbacks);
  refs.pcRef.current = pc;

  const tracks = refs.streamRef.current.getTracks();
  tracks.forEach(t => pc.addTrack(t, refs.streamRef.current!));
  console.log(`[DEVICE-PC] 🎬 ${tracks.length} track(s) ajouté(s): ${tracks.map(t => `${t.kind}(${t.label})`).join(', ')}`);
  callbacks.addSdpLog({
    direction: 'info',
    type: 'event',
    label: `🎬 Tracks ajoutés: ${tracks.map(t => `${t.kind}(${t.label})`).join(', ')}`,
  });

  const offer = await pc.createOffer();
  if (!offer.sdp) return;

  const rawSummary = summarizeSdp(offer.sdp, 'Offer brute');
  console.log(`[DEVICE-PC] ↑ ${rawSummary}`);
  callbacks.addSdpLog({ direction: 'sent', type: 'offer', label: rawSummary, sdp: offer.sdp, codec: parseSdpCodec(offer.sdp) });

  offer.sdp = forceDeviceCodec(offer.sdp, refs.requiredCodecRef.current);

  const filteredSummary = summarizeSdp(offer.sdp, 'Offer filtrée → régie');
  console.log(`[DEVICE-PC] ↑ ${filteredSummary}`);
  callbacks.addSdpLog({ direction: 'sent', type: 'offer', label: filteredSummary, sdp: offer.sdp, codec: parseSdpCodec(offer.sdp) });

  await pc.setLocalDescription(offer);
  console.log(`[DEVICE-PC] ↑ Offer envoyée (signalingState: ${pc.signalingState})`);
  sendDeviceOffer(refs.wsRef, offer);
};

// ============================================================================
// SECTION D — Réception Offer régie → device
// Perfect Negotiation : le device est POLITE
// Il accepte le rollback si une collision se produit.
// ============================================================================

/**
 * Rattache au besoin les pistes de la capture locale à la PeerConnection.
 *
 * Sans ce filet, une PC créée à un moment où `streamRef.current` était encore
 * null restait DÉFINITIVEMENT sans émetteur : les branches qui ajoutent les
 * pistes sont toutes des branches de CRÉATION de PC, et une Offer ultérieure
 * retombe sur « PC déjà stable », qui n'ajoute rien. La régie recevait alors
 * une Answer `dir:inactive` à chaque tour — connexion établie, DTLS valide,
 * badge « Connecté », et pas un octet de média. Constaté le 15/08/2026.
 *
 * Appelé APRÈS `setRemoteDescription` et AVANT `createAnswer` : `addTrack`
 * rattache alors la piste au transceiver déjà négocié pour ce kind, ce qui fait
 * passer la m-line de `inactive` à `sendonly` dans l'Answer.
 */
const ensureLocalTracksAttached = (
  pc: RTCPeerConnection,
  refs: DeviceWebRTCRefs,
  callbacks: DeviceWebRTCCallbacks,
): void => {
  const stream = refs.streamRef.current;
  if (!stream) return;

  const kindsAlreadySent = new Set(
    pc.getSenders().map(s => s.track?.kind).filter(Boolean) as string[]
  );

  const added: string[] = [];
  stream.getTracks().forEach(track => {
    if (kindsAlreadySent.has(track.kind)) return;
    try {
      pc.addTrack(track, stream);
      added.push(`${track.kind}(${track.label || 'sans label'})`);
    } catch (e: any) {
      console.warn(`[DEVICE-PC] ⚠ addTrack ${track.kind} refusé: ${e?.message ?? e}`);
    }
  });

  if (added.length === 0) return;

  console.warn(`[DEVICE-PC] 🩹 Pistes rattachées à une PC qui n'en avait pas : ${added.join(', ')}`);
  callbacks.addSdpLog({
    direction: 'info', type: 'event',
    label: `🩹 Pistes rattachées à une PC sans émetteur : ${added.join(', ')}`,
  });
};

export const handleDeviceRemoteOffer = async (
  offer: RTCSessionDescriptionInit,
  meta: { required_codec?: RequiredCodec } | undefined,
  refs: DeviceWebRTCRefs,
  callbacks: DeviceWebRTCCallbacks
): Promise<void> => {

  // ── Refus d'une Offer arrivée avant la capture ────────────────────────────
  //
  // Répondre ici produirait une Answer `dir:inactive` — une négociation qui
  // aboutit sans média, que rien ne rejoue ensuite. Mieux vaut ne pas répondre :
  // la régie garde sa PC en `have-local-offer`, son watchdog la rejoue au bout
  // de 20 s, et surtout le `device_connected` qui suivra `ready` déclenchera une
  // Offer propre, cette fois avec la caméra prête.
  if (!refs.streamRef.current) {
    console.warn('[DEVICE-PC] ⏭ Offer régie ignorée — capture caméra pas encore prête (Answer inactive évitée)');
    callbacks.addSdpLog({
      direction: 'info', type: 'event',
      label: '⏭ Offer régie ignorée — caméra pas encore capturée, la régie réémettra après « ready »',
    });
    return;
  }

  if (meta?.required_codec) {
    refs.requiredCodecRef.current = meta.required_codec;
    console.log(`[DEVICE-PC] 🔒 Codec imposé par régie: ${meta.required_codec.codec} profil=${meta.required_codec.profile}`);
    callbacks.addSdpLog({
      direction: 'info', type: 'event',
      label: `🔒 Codec imposé par régie: ${meta.required_codec.codec} profil=${meta.required_codec.profile}`,
    });
  }

  // Log de l'Offer reçue avec détail SDP
  const offerSummary = summarizeSdp(offer.sdp, 'Offer ← régie');
  console.log(`[DEVICE-PC] ↓ ${offerSummary}`);

  const pc = refs.pcRef.current;

  // ── Perfect Negotiation : rollback si collision ───────────────────────────
  // Le device est POLITE : si on reçoit une Offer alors qu'on est déjà en
  // train de négocier (have-local-offer ou have-remote-offer), on rollback
  // notre état local et on accepte l'Offer de la régie (impolite peer).
  const offerCollision =
    pc !== null &&
    (pc.signalingState === 'have-local-offer' ||
     pc.signalingState === 'have-remote-offer');

  if (offerCollision) {
    console.warn(`[DEVICE-PC] ⚠️ COLLISION d'Offer détectée ! signalingState=${pc!.signalingState} → rollback (peer poli)`);
    callbacks.addSdpLog({
      direction: 'info', type: 'event',
      label: `⚠️ COLLISION Offer (signalingState=${pc!.signalingState}) → rollback peer poli`,
    });

    try {
      await pc!.setLocalDescription({ type: 'rollback' });
      console.log('[DEVICE-PC] ✅ Rollback réussi → application de la nouvelle Offer régie');
      callbacks.addSdpLog({
        direction: 'info', type: 'event',
        label: '✅ Rollback réussi → application Offer régie',
      });
      // La PC est maintenant de retour à stable, on continue avec la même PC
    } catch (rollbackErr: any) {
      console.error(`[DEVICE-PC] ❌ Rollback échoué: ${rollbackErr?.message} — recréation PC`);
      callbacks.addSdpLog({
        direction: 'received', type: 'error',
        label: `❌ Rollback échoué: ${rollbackErr?.message} — recréation PC`,
      });
      // Rollback impossible (vieux navigateur / état irrécupérable) : recréer la PC
      pc!.close();
      refs.pcRef.current = null;
      const freshPc = makeDevicePeerConnection(refs, callbacks);
      refs.pcRef.current = freshPc;
      if (refs.streamRef.current) {
        refs.streamRef.current.getTracks().forEach(t => freshPc.addTrack(t, refs.streamRef.current!));
        console.log('[DEVICE-PC] 🎬 Tracks ré-ajoutés sur nouvelle PC (après rollback échoué)');
      }
    }

  } else if (!pc) {
    // Pas de PC existante — première Offer reçue de la régie
    console.log('[DEVICE-PC] 🔧 Première Offer régie → création PC');
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: '🔧 Création PC (première Offer régie)' });
    const freshPc = makeDevicePeerConnection(refs, callbacks);
    refs.pcRef.current = freshPc;
    if (refs.streamRef.current) {
      refs.streamRef.current.getTracks().forEach(t => freshPc.addTrack(t, refs.streamRef.current!));
      const tracks = refs.streamRef.current.getTracks();
      console.log(`[DEVICE-PC] 🎬 ${tracks.length} track(s) ajouté(s): ${tracks.map(t => `${t.kind}(${t.label})`).join(', ')}`);
      callbacks.addSdpLog({
        direction: 'info', type: 'event',
        label: `🎬 Tracks ajoutés: ${tracks.map(t => `${t.kind}(${t.label})`).join(', ')}`,
      });
    }

  } else if (pc.signalingState === 'stable') {
    // Renégociation sur PC stable existante (ex: changement de caméra côté régie)
    console.log('[DEVICE-PC] 🔄 Renégociation sur PC stable existante');
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: '🔄 Renégociation — PC déjà stable, application Offer' });

  } else {
    // État inattendu non géré par le rollback — recréation défensive
    console.warn(`[DEVICE-PC] ⚠️ État inattendu: signalingState=${pc.signalingState} → recréation PC`);
    callbacks.addSdpLog({
      direction: 'info', type: 'event',
      label: `⚠️ État inattendu (signalingState=${pc.signalingState}) → recréation PC`,
    });
    pc.close();
    refs.pcRef.current = null;
    const freshPc = makeDevicePeerConnection(refs, callbacks);
    refs.pcRef.current = freshPc;
    if (refs.streamRef.current) {
      refs.streamRef.current.getTracks().forEach(t => freshPc.addTrack(t, refs.streamRef.current!));
    }
  }

  const currentPc = refs.pcRef.current!;

  // ── Application de l'Offer ────────────────────────────────────────────────
  await currentPc.setRemoteDescription(offer);
  ensureLocalTracksAttached(currentPc, refs, callbacks);
  refs.remoteDescSetRef.current = true;
  console.log(`[DEVICE-PC] ✅ RemoteDescription (Offer régie) appliquée — signalingState: ${currentPc.signalingState}`);
  callbacks.addSdpLog({
    direction: 'received', type: 'offer',
    label: offerSummary,
    sdp: offer.sdp,
    codec: offer.sdp ? parseSdpCodec(offer.sdp) : undefined,
  });

  // Flush ICE buffer (candidats arrivés avant la remote description)
  await flushDeviceIceCandidateBuffer(currentPc, refs, callbacks);

  // ── Création de l'Answer ──────────────────────────────────────────────────
  const answer = await currentPc.createAnswer();
  if (answer.sdp) {
    const rawAnswerSummary = summarizeSdp(answer.sdp, 'Answer brute');
    console.log(`[DEVICE-PC] ↑ ${rawAnswerSummary}`);
    callbacks.addSdpLog({ direction: 'sent', type: 'answer', label: rawAnswerSummary, sdp: answer.sdp, codec: parseSdpCodec(answer.sdp) });

    answer.sdp = forceDeviceCodec(answer.sdp, refs.requiredCodecRef.current);

    const filteredAnswerSummary = summarizeSdp(answer.sdp, 'Answer filtrée → régie');
    console.log(`[DEVICE-PC] ↑ ${filteredAnswerSummary}`);
    callbacks.addSdpLog({ direction: 'sent', type: 'answer', label: filteredAnswerSummary, sdp: answer.sdp, codec: parseSdpCodec(answer.sdp) });

    const neg = extractCodecFromSdp(answer.sdp);
    if (refs.requiredCodecRef.current?.profile) {
      neg.videoProfile = refs.requiredCodecRef.current.profile;
    }
    callbacks.onNegotiatedCodecs(neg);
  }

  await currentPc.setLocalDescription(answer);
  console.log(`[DEVICE-PC] ↑ Answer envoyée (signalingState: ${currentPc.signalingState})`);
  sendDeviceAnswer(refs.wsRef, answer);
};

// ============================================================================
// SECTION E — Réception Answer régie → device
// ============================================================================

export const handleDeviceRemoteAnswer = async (
  answer: RTCSessionDescriptionInit,
  refs: DeviceWebRTCRefs,
  callbacks: DeviceWebRTCCallbacks
): Promise<void> => {
  if (!refs.pcRef.current) {
    console.warn('[DEVICE-PC] ⚠ handleDeviceRemoteAnswer: pas de PC');
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: '⚠ Answer reçue mais pas de PC — ignorée' });
    return;
  }

  // Perfect Negotiation : ignorer une Answer qui arrive dans un mauvais état
  // (Answer orpheline après rollback ou recréation de PC)
  if (refs.pcRef.current.signalingState !== 'have-local-offer') {
    console.warn(`[DEVICE-PC] ⚠ Answer ignorée — signalingState=${refs.pcRef.current.signalingState} (attendu: have-local-offer)`);
    callbacks.addSdpLog({
      direction: 'info', type: 'event',
      label: `⚠ Answer ignorée (signalingState=${refs.pcRef.current.signalingState} ≠ have-local-offer) — Answer orpheline ?`,
    });
    return;
  }

  if (answer.sdp) {
    const neg = extractCodecFromSdp(answer.sdp);
    const answerSummary = summarizeSdp(answer.sdp, 'Answer ← régie');
    console.log(`[DEVICE-PC] ↓ ${answerSummary}`);
    callbacks.addSdpLog({
      direction: 'received', type: 'answer',
      label: answerSummary,
      sdp:   answer.sdp,
      codec: { video: neg.videoCodec || undefined, profile: neg.videoProfile || undefined, audio: neg.audioCodec || undefined },
    });
    callbacks.onNegotiatedCodecs(neg);
  }

  await refs.pcRef.current.setRemoteDescription(answer);
  refs.remoteDescSetRef.current = true;
  console.log(`[DEVICE-PC] ✅ Answer appliquée — signalingState: ${refs.pcRef.current.signalingState}`);
  callbacks.addSdpLog({ direction: 'info', type: 'event', label: `✅ Answer appliquée — signalingState: ${refs.pcRef.current.signalingState}` });

  await flushDeviceIceCandidateBuffer(refs.pcRef.current, refs, callbacks);
};

// ============================================================================
// SECTION F — Candidat ICE entrant (régie → device)
// ============================================================================

export const handleDeviceRemoteIceCandidate = async (
  candidate: RTCIceCandidateInit,
  refs: Pick<DeviceWebRTCRefs, 'pcRef' | 'iceCandidateBufferRef' | 'remoteDescSetRef'>
): Promise<void> => {
  const summary = summarizeIceCandidate(candidate);

  if (!refs.pcRef.current || !refs.remoteDescSetRef.current) {
    refs.iceCandidateBufferRef.current.push(candidate);
    console.log(`[DEVICE-PC] 🧊 ICE entrant bufférisé (${summary}) — PC=${refs.pcRef.current ? 'ok' : 'null'} remoteDesc=${refs.remoteDescSetRef.current} — buffer: ${refs.iceCandidateBufferRef.current.length}`);
    return;
  }

  console.log(`[DEVICE-PC] 🧊 ICE entrant appliqué: ${summary}`);
  try {
    await refs.pcRef.current.addIceCandidate(new RTCIceCandidate(candidate));
  } catch (err) {
    console.warn(`[DEVICE-PC] ⚠ addIceCandidate échoué (${summary}):`, err);
  }
};

// ============================================================================
// SECTION G — Flush buffer ICE (interne)
// ============================================================================

const flushDeviceIceCandidateBuffer = async (
  pc: RTCPeerConnection,
  refs: Pick<DeviceWebRTCRefs, 'iceCandidateBufferRef'>,
  callbacks: Pick<DeviceWebRTCCallbacks, 'addSdpLog'>
): Promise<void> => {
  const buf = refs.iceCandidateBufferRef.current;
  if (!buf.length) return;

  console.log(`[DEVICE-PC] 🧊 Flush buffer ICE: ${buf.length} candidat(s)`);
  callbacks.addSdpLog({
    direction: 'info', type: 'event',
    label: `🧊 Flush ${buf.length} ICE candidat(s) bufférisé(s)`,
  });

  refs.iceCandidateBufferRef.current = [];
  for (const c of buf) {
    const summary = summarizeIceCandidate(c);
    try {
      await pc.addIceCandidate(new RTCIceCandidate(c));
      console.log(`[DEVICE-PC] 🧊 ICE flush appliqué: ${summary}`);
    } catch (err) {
      console.warn(`[DEVICE-PC] ⚠ addIceCandidate flush échoué (${summary}):`, err);
    }
  }
};

// ============================================================================
// SECTION H — replaceTrack à chaud
// ============================================================================

export const replaceDeviceTrack = async (
  pc: RTCPeerConnection,
  newTrack: MediaStreamTrack,
  callbacks: Pick<DeviceWebRTCCallbacks, 'addSdpLog'>
): Promise<void> => {
  if (pc.connectionState !== 'connected') {
    console.warn(`[DEVICE-PC] ⚠ replaceTrack ignoré — connectionState=${pc.connectionState}`);
    return;
  }
  const sender = pc.getSenders().find(s => s.track?.kind === newTrack.kind);
  if (sender) {
    const oldLabel = sender.track?.label ?? '(null)';
    await sender.replaceTrack(newTrack);
    console.log(`[DEVICE-PC] 🔄 replaceTrack ${newTrack.kind}: "${oldLabel}" → "${newTrack.label}"`);
    callbacks.addSdpLog({
      direction: 'info', type: 'event',
      label: `🔄 replaceTrack ${newTrack.kind}: "${oldLabel}" → "${newTrack.label}"`,
    });

    // Forcer un keyframe immédiat pour éviter le freeze côté régie
    if (newTrack.kind === 'video') {
      try {
        const params = sender.getParameters();
        await sender.setParameters(params);
        console.log('[DEVICE-PC] 🔑 Keyframe forcé après replaceTrack');
        callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🔑 Keyframe forcé après replaceTrack` });
      } catch { /* silencieux — pas critique */ }
    }
  } else {
    console.warn(`[DEVICE-PC] ⚠ replaceTrack: aucun sender trouvé pour kind=${newTrack.kind}`);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `⚠ replaceTrack: aucun sender ${newTrack.kind} trouvé` });
  }
};

// ============================================================================
// SECTION I — Fermeture PeerConnection
// ============================================================================

export const closeDevicePeerConnection = (
  refs: Pick<DeviceWebRTCRefs, 'pcRef' | 'iceCandidateBufferRef' | 'remoteDescSetRef'>,
  callbacks: Pick<DeviceWebRTCCallbacks, 'addSdpLog'>
): void => {
  if (refs.pcRef.current) {
    const state = refs.pcRef.current.connectionState;
    refs.pcRef.current.close();
    refs.pcRef.current = null;
    console.log(`[DEVICE-PC] ⏹ PeerConnection fermée (était: ${state})`);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `⏹ PeerConnection fermée (état avant: ${state})` });
  }
  refs.iceCandidateBufferRef.current = [];
  refs.remoteDescSetRef.current      = false;
};

// ============================================================================
// SECTION J — PC retour côté device (régie → device)
// ============================================================================

export const makeDeviceReturnPeerConnection = (
  pcReturnRef     : MutableRefObject<RTCPeerConnection | null>,
  returnStreamRef : MutableRefObject<MediaStream | null>,
  iceBufReturnRef : MutableRefObject<RTCIceCandidateInit[]>,
  wsRef           : MutableRefObject<WebSocket | null>,
  callbacks: Pick<DeviceWebRTCCallbacks, 'addSdpLog'>,
  onStreamReady   : (stream: MediaStream, trackKind: 'audio' | 'video') => void,
): RTCPeerConnection => {
  iceBufReturnRef.current = [];

  const pc = new RTCPeerConnection(defaultRTCConfig());
  pcReturnRef.current = pc;

  console.log('[DEVICE-PC-RETURN] 🔧 Nouvelle PC retour créée');
  callbacks.addSdpLog({ direction: 'info', type: 'event', label: '↩ PC retour créée' });

  pc.ontrack = (event) => {
    const t = event.track;
    console.log(`[DEVICE-PC-RETURN] 📹 Track retour reçu: ${t.kind} readyState=${t.readyState} muted=${t.muted}`);
    callbacks.addSdpLog({
      direction: 'received', type: 'event',
      label: `↩ Track retour reçu: ${t.kind} (readyState=${t.readyState})`,
    });
    if (event.streams[0]) {
      returnStreamRef.current = event.streams[0];
      onStreamReady(event.streams[0], t.kind as 'audio' | 'video');
    }
  };

  pc.onicecandidate = (event) => {
    if (event.candidate) {
      const summary = summarizeIceCandidate(event.candidate);
      console.log(`[DEVICE-PC-RETURN] 🧊 ICE sortant: ${summary}`);
      callbacks.addSdpLog({ direction: 'sent', type: 'event', label: `↩ ICE retour sortant: ${summary}` });
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type      : 'ice-candidate',
          target    : 'control',
          stream_id : 'return',
          payload   : event.candidate,
        }));
      }
    } else {
      console.log('[DEVICE-PC-RETURN] 🧊 ICE gathering terminé');
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: '↩ ICE retour gathering terminé' });
    }
  };

  pc.onconnectionstatechange = () => {
    const s = pc.connectionState;
    const icon = s === 'connected' ? '✅' : s === 'failed' ? '❌' : s === 'disconnected' ? '⚠️' : '🔄';
    console.log(`[DEVICE-PC-RETURN] 🔗 connectionState: ${s}`);
    callbacks.addSdpLog({
      direction: s === 'failed' || s === 'disconnected' ? 'received' : 'info',
      type: s === 'failed' ? 'error' : 'event',
      label: `${icon} ↩ Retour PC: ${s}`,
    });
    if (s === 'failed' || s === 'closed') {
      pcReturnRef.current = null;
    }
  };

  pc.onicegatheringstatechange = () => {
    console.log(`[DEVICE-PC-RETURN] 🧊 ICE gathering retour: ${pc.iceGatheringState}`);
  };

  return pc;
};

export const handleDeviceReturnOffer = async (
  offer           : RTCSessionDescriptionInit,
  pcReturnRef     : MutableRefObject<RTCPeerConnection | null>,
  returnStreamRef : MutableRefObject<MediaStream | null>,
  iceBufReturnRef : MutableRefObject<RTCIceCandidateInit[]>,
  wsRef           : MutableRefObject<WebSocket | null>,
  callbacks: Pick<DeviceWebRTCCallbacks, 'addSdpLog'>,
  onStreamReady   : (stream: MediaStream, trackKind: 'audio' | 'video') => void,
): Promise<void> => {
  if (pcReturnRef.current) {
    console.log('[DEVICE-PC-RETURN] 🔄 Fermeture PC retour existante');
    pcReturnRef.current.close();
    pcReturnRef.current = null;
  }

  const offerSummary = summarizeSdp(offer.sdp, 'Offer retour ← régie');
  console.log(`[DEVICE-PC-RETURN] ↓ ${offerSummary}`);
  callbacks.addSdpLog({
    direction: 'received', type: 'offer',
    label: `↩ ${offerSummary}`,
    sdp: offer.sdp,
    codec: offer.sdp ? parseSdpCodec(offer.sdp) : undefined,
  });

  const pc = makeDeviceReturnPeerConnection(
    pcReturnRef, returnStreamRef, iceBufReturnRef, wsRef, callbacks, onStreamReady,
  );

  await pc.setRemoteDescription(offer);
  console.log('[DEVICE-PC-RETURN] ✅ RemoteDescription retour appliquée');

  const buf = iceBufReturnRef.current;
  if (buf.length) {
    console.log(`[DEVICE-PC-RETURN] 🧊 Flush ${buf.length} ICE retour bufférisé(s)`);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `↩ Flush ${buf.length} ICE retour bufférisé(s)` });
    iceBufReturnRef.current = [];
    for (const c of buf) {
      const summary = summarizeIceCandidate(c);
      try { await pc.addIceCandidate(new RTCIceCandidate(c)); }
      catch (err) { console.warn(`[DEVICE-PC-RETURN] ⚠ addIceCandidate flush retour échoué (${summary}):`, err); }
    }
  }

  const answer = await pc.createAnswer();
  await pc.setLocalDescription(answer);

  const answerSummary = summarizeSdp(answer.sdp, 'Answer retour → régie');
  console.log(`[DEVICE-PC-RETURN] ↑ ${answerSummary}`);
  callbacks.addSdpLog({
    direction: 'sent', type: 'answer',
    label: `↩ ${answerSummary}`,
    sdp: answer.sdp,
    codec: answer.sdp ? parseSdpCodec(answer.sdp) : undefined,
  });

  if (wsRef.current?.readyState === WebSocket.OPEN) {
    wsRef.current.send(JSON.stringify({
      type      : 'answer',
      target    : 'control',
      stream_id : 'return',
      payload   : answer,
    }));
  }
};

export const handleDeviceReturnIceCandidate = async (
  candidate       : RTCIceCandidateInit,
  pcReturnRef     : MutableRefObject<RTCPeerConnection | null>,
  iceBufReturnRef : MutableRefObject<RTCIceCandidateInit[]>,
): Promise<void> => {
  const summary = summarizeIceCandidate(candidate);
  if (!pcReturnRef.current) {
    iceBufReturnRef.current.push(candidate);
    console.log(`[DEVICE-PC-RETURN] 🧊 ICE retour entrant bufférisé: ${summary} — buffer: ${iceBufReturnRef.current.length}`);
    return;
  }
  console.log(`[DEVICE-PC-RETURN] 🧊 ICE retour entrant appliqué: ${summary}`);
  try { await pcReturnRef.current.addIceCandidate(new RTCIceCandidate(candidate)); }
  catch (err) { console.warn(`[DEVICE-PC-RETURN] ⚠ addIceCandidate retour échoué (${summary}):`, err); }
};

export const closeDeviceReturnPeerConnection = (
  pcReturnRef     : MutableRefObject<RTCPeerConnection | null>,
  iceBufReturnRef : MutableRefObject<RTCIceCandidateInit[]>,
  callbacks: Pick<DeviceWebRTCCallbacks, 'addSdpLog'>,
): void => {
  if (pcReturnRef.current) {
    const state = pcReturnRef.current.connectionState;
    pcReturnRef.current.close();
    pcReturnRef.current = null;
    console.log(`[DEVICE-PC-RETURN] ⏹ PC retour fermée (était: ${state})`);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `↩ PC retour fermée (état avant: ${state})` });
  }
  iceBufReturnRef.current = [];
};